import { useEffect, useMemo, useState } from 'react'
import { calculateLeasing } from '../utils/leasingEngine'
import { fetchWithTimeout } from '../utils/fetchWithTimeout'

import { BACKEND_URL as BACKEND } from '../config/backendUrl'
const CONSENT_KEY = 'radar_leasing_public_consent_v1'

function formatChf(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return `${Math.round(n).toLocaleString('fr-CH')} CHF`
}

export function PublicLeasingProposalPage({ proposalId }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [proposal, setProposal] = useState(null)
  const [downPct, setDownPct] = useState(20)
  const [durationMonths, setDurationMonths] = useState(36)
  const [consent, setConsent] = useState(() => localStorage.getItem(CONSENT_KEY) === 'yes')
  const [leadName, setLeadName] = useState('')
  const [leadPhone, setLeadPhone] = useState('')
  const [leadSlot, setLeadSlot] = useState('')
  const [leadSent, setLeadSent] = useState(false)

  useEffect(() => {
    let stop = false
    ;(async () => {
      try {
        setLoading(true)
        const r = await fetchWithTimeout(`${BACKEND}/api/leasing/proposals/${encodeURIComponent(proposalId)}/public`)
        const d = await r.json()
        if (!r.ok) throw new Error(d?.error || 'proposal_not_found')
        if (stop) return
        setProposal(d)
        const i = d?.inputs || {}
        setDownPct(Number(i.downPaymentPct ?? 20))
        setDurationMonths(Number(i.durationMonths ?? 36))
      } catch (e) {
        if (!stop) setError(e?.message || 'Erreur chargement proposition')
      } finally {
        if (!stop) setLoading(false)
      }
    })()
    return () => {
      stop = true
    }
  }, [proposalId])

  const calc = useMemo(() => {
    if (!proposal) return null
    const i = proposal.inputs || {}
    const vehiclePrice = Number(i.vehiclePrice)
    const rate = Number(i.nominalRate)
    const annualKm = Number(i.annualKm)
    const residual = Number(proposal?.outputs?.residualValue)
    if (![24, 36, 48, 60].includes(durationMonths) || !Number.isFinite(vehiclePrice) || !Number.isFinite(rate) || !Number.isFinite(annualKm) || !Number.isFinite(residual)) return null
    const downPayment = Math.round((vehiclePrice * downPct) / 100)
    try {
      return calculateLeasing({
        vehiclePrice,
        downPayment,
        durationMonths,
        annualKm,
        nominalRate: rate,
        residualValue: residual,
        insuranceMonthly: 0,
      })
    } catch {
      return null
    }
  }, [proposal, downPct, durationMonths])

  const track = async (type, payload) => {
    try {
      await fetchWithTimeout(`${BACKEND}/api/leasing/proposals/${encodeURIComponent(proposalId)}/public/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, payload, consent }),
      })
    } catch {
      // no-op
    }
  }

  if (loading) return <div style={{ minHeight: '100vh', background: '#f5f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Chargement…</div>
  if (error || !proposal) return <div style={{ minHeight: '100vh', background: '#f5f7fb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#991b1b' }}>{error || 'Proposition introuvable'}</div>

  const v = proposal.vehicle_snapshot || {}
  const i = proposal.inputs || {}

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fb', color: '#0f172a', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: 18 }}>
        <header style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="" style={{ width: 28, height: 28 }} />
            <div style={{ fontWeight: 700 }}>{proposal?.dealer?.name || 'Concessionnaire'}</div>
          </div>
          <div style={{ fontSize: 12, color: '#475569' }}>Proposition leasing</div>
        </header>

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 12 }}>
          <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
            {v.image_url ? <img src={v.image_url} alt="" style={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} /> : null}
            <h2 style={{ marginTop: 10, marginBottom: 6, fontSize: 22 }}>{v.titre || `${v.make || ''} ${v.model || ''}`}</h2>
            <div style={{ fontSize: 13, color: '#475569' }}>
              {v.annee || i.annee} · {Number(v.km ?? i.km ?? 0).toLocaleString('fr-CH')} km · Prix {formatChf(i.vehiclePrice)}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>
              Taux nominal {Number(i.nominalRate).toFixed(1)}% · Km annuels {Number(i.annualKm).toLocaleString('fr-CH')}
            </div>
          </section>

          <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Simulateur</div>
            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 6 }}>Acompte (%)</label>
              <input
                type="range"
                min="0"
                max="40"
                step="1"
                value={downPct}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  setDownPct(val)
                  track('simulation_change', { downPct: val, durationMonths })
                }}
                style={{ width: '100%' }}
              />
              <div style={{ marginTop: 4, fontSize: 12 }}>{downPct}%</div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#475569', marginBottom: 6 }}>Durée</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[24, 36, 48, 60].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      setDurationMonths(d)
                      track('simulation_change', { downPct, durationMonths: d })
                    }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: `1px solid ${durationMonths === d ? '#1d4ed8' : '#cbd5e1'}`,
                      background: durationMonths === d ? '#eff6ff' : '#fff',
                      color: durationMonths === d ? '#1e3a8a' : '#334155',
                      cursor: 'pointer',
                    }}
                  >
                    {d} mois
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 34, fontWeight: 300, lineHeight: 1 }}>{formatChf(calc?.monthlyPayment)}</div>
            <div style={{ marginTop: 4, fontSize: 12, color: '#475569' }}>TAEG {Number(calc?.taeg || 0).toFixed(2)}% · Coût total {formatChf(calc?.totalLeasingCost)}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>Valeur résiduelle (verrouillée): {formatChf(proposal?.outputs?.residualValue)}</div>

            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Être rappelé</div>
              <input value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Nom" style={{ width: '100%', marginBottom: 8, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <input value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} placeholder="Téléphone" style={{ width: '100%', marginBottom: 8, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <input value={leadSlot} onChange={(e) => setLeadSlot(e.target.value)} placeholder="Créneau préféré" style={{ width: '100%', marginBottom: 8, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <button
                type="button"
                onClick={async () => {
                  if (!leadName.trim() || !leadPhone.trim()) return
                  await track('lead', { name: leadName, phone: leadPhone, slot: leadSlot })
                  setLeadSent(true)
                }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
              >
                Être rappelé
              </button>
              {leadSent ? <div style={{ marginTop: 8, fontSize: 12, color: '#166534' }}>Merci, la concession vous recontacte rapidement.</div> : null}
            </div>
          </section>
        </div>

        {!consent ? (
          <div style={{ position: 'fixed', left: 12, right: 12, bottom: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.08)' }}>
            <div style={{ fontSize: 12, color: '#334155' }}>
              Nous utilisons uniquement un cookie local pour suivre vos modifications du simulateur (pas de cookies tiers), conformément à la nLPD.
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConsent(false)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                Refuser
              </button>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem(CONSENT_KEY, 'yes')
                  setConsent(true)
                }}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', cursor: 'pointer' }}
              >
                Accepter
              </button>
            </div>
          </div>
        ) : null}

        <footer style={{ marginTop: 14, fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
          Proposition indicative. L’octroi d’un leasing est soumis à examen de solvabilité selon la LCC. Le TAEG effectif peut varier selon votre profil.
        </footer>
      </div>
    </div>
  )
}
